import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PublicLayout from "../shell/PublicLayout";
import ListRow from "../components/ListRow";
import ProgressBar from "../components/ProgressBar";
import Badge from "../components/Badge";
import { LikeButton, BookmarkButton } from "../components/EngagementButtons";
import { PlayIcon } from "../components/icons";
import { fmt } from "../player/MiniPlayer";
import { isSignedIn, flushPositions } from "../api/progressSync";
import { rememberAuthReturn } from "../api/engagement";
import { logoutUserData } from "../../store/slices/UserDataSlice";
import { paths } from "../routing/paths";

/*
 * Account dashboard per `Account Dashboard.dc.html` (brief v2.2 §7.2.11).
 * Habit UI, compact and stable: identity header (initials avatar, name,
 * "Member since Adar 5786") → Continue where you left off (progress bar +
 * "26 min left" + Resume) → Bookmarks (mixed SERIES + class rows) →
 * Liked → quiet footer. Every section has a designed empty state
 * (dashed card, warm copy) — nothing renders blank. One authenticated
 * GET /user/dashboard supplies everything; until that endpoint deploys
 * the page renders the designed empty states.
 */

const BASE = process.env.REACT_APP_API || "https://api.insidechassidus.org/api";

function initialsOf(name, email) {
  const source = (name || "").trim() || (email || "");
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (source.slice(0, 2) || "IC").toUpperCase();
}

function SectionHead({ title, viewAllTo }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <h2 className="m-0 text-[12px] font-bold leading-4 tracking-[0.5px] uppercase text-ic-slate">{title}</h2>
      {viewAllTo && (
        <Link to={viewAllTo} className="text-[14px] leading-5 text-ic-blue font-semibold inline-flex items-center min-h-[44px] hover:underline">
          View all →
        </Link>
      )}
    </div>
  );
}

function EmptySection({ icon, children, action }) {
  return (
    <div className="bg-white border border-dashed border-ic-borderGray rounded-xl p-6 flex flex-col gap-3 items-start">
      <div className="flex items-center gap-3">
        {icon && (
          <span className="flex-shrink-0 w-11 h-11 rounded-full bg-[rgba(24,108,206,0.08)] text-ic-blue inline-flex items-center justify-center">{icon}</span>
        )}
        <p className="m-0 text-[18px] leading-7 text-ic-body" style={{ textWrap: "pretty" }}>
          {children}
        </p>
      </div>
      {action}
    </div>
  );
}

const heartIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 21s-8-5.3-8-11a4.5 4.5 0 0 1 8-2.8A4.5 4.5 0 0 1 20 10c0 5.7-8 11-8 11z" />
  </svg>
);
const bookmarkIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 4h12v17l-6-4-6 4z" />
  </svg>
);

export default function AccountDashboardPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState(null); // null = loading; {} = unavailable

  // Live player state for the real-time overlay + pause-triggered refresh.
  const refreshRef = React.useRef(null);
  const wasPlayingRef = React.useRef(false);
  const playerCurrent = useSelector((s) => s.player.current);
  const playerPlaying = useSelector((s) => s.player.playing);
  const playerPosition = useSelector((s) => s.player.position);
  const playerDuration = useSelector((s) => s.player.duration);

  // The player pushes positions on a debounce (pause / 15s heartbeat), so a
  // plain read here would usually miss the class the user JUST played.
  // Three layers keep the dashboard current:
  //   1. flush pending position writes, THEN read (mount + every refresh);
  //   2. refresh on tab focus and whenever playback pauses;
  //   3. overlay the live player position on the matching row (below) so
  //      the currently playing class ticks in real time with no network.
  useEffect(() => {
    if (!isSignedIn()) {
      rememberAuthReturn(paths.profile());
      navigate(paths.signIn(), { replace: true });
      return;
    }
    let cancelled = false;
    const refresh = async () => {
      try {
        await flushPositions();
        const token = localStorage.getItem("accessToken");
        const res = await fetch(`${BASE}/user/dashboard`, {
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        });
        const json = res.ok ? await res.json() : null;
        if (!cancelled) setData((prev) => (json?.status ? json.data : prev ?? {}));
      } catch (_) {
        if (!cancelled) setData((prev) => prev ?? {});
      }
    };
    refresh();
    const onFocus = () => refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    refreshRef.current = refresh;
    return () => {
      cancelled = true;
      refreshRef.current = null;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [navigate]);

  useEffect(() => {
    // Playback just paused → its position push is pending; flush + re-read.
    if (wasPlayingRef.current && !playerPlaying) {
      const t = setTimeout(() => refreshRef.current?.(), 400);
      wasPlayingRef.current = playerPlaying;
      return () => clearTimeout(t);
    }
    wasPlayingRef.current = playerPlaying;
    return undefined;
  }, [playerPlaying]);

  const signOut = () => {
    try {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("loginData");
      dispatch(logoutUserData());
    } catch (_) {}
    navigate(paths.home());
  };

  const stored = (() => {
    try {
      return JSON.parse(localStorage.getItem("loginData") || "{}");
    } catch (_) {
      return {};
    }
  })();
  const profile = data?.profile || { name: stored?.name, email: stored?.email, member_since: null };
  const bookmarks = data?.bookmarks || [];
  const liked = data?.liked || [];

  // Layer 3: live overlay. The row for the class that's loaded in the
  // player follows the player's position second-by-second; if the user
  // started a class the server list doesn't have yet, it appears on top.
  const continueRows = (() => {
    const rows = [...(data?.continue || [])];
    const liveId = playerCurrent?.classId;
    if (!liveId || !(playerPosition > 0)) return rows;
    const idx = rows.findIndex((r) => Number(r.id) === Number(liveId));
    if (idx >= 0) {
      const row = { ...rows[idx], position_seconds: Math.floor(playerPosition) };
      if (playerDuration > 0) row.duration_seconds = Math.round(playerDuration);
      rows.splice(idx, 1);
      rows.unshift(row); // most recently played first
    } else if (data?.continue) {
      rows.unshift({
        kind: "class",
        id: liveId,
        title: playerCurrent.title,
        slug: String(playerCurrent.href || "").replace(/^\//, "") || null,
        media: playerCurrent.mediaKind || "audio",
        duration_seconds: playerDuration > 0 ? Math.round(playerDuration) : playerCurrent.durationHint || null,
        position_seconds: Math.floor(playerPosition),
        pill: playerCurrent.seriesTitle ? { title: playerCurrent.seriesTitle, slug: null, type: "series" } : null,
      });
    }
    return rows;
  })();

  const pillNode = (item) =>
    item.kind === "series" ? (
      <Badge variant="primary">SERIES</Badge>
    ) : item.pill ? (
      <Badge>{item.pill.title}</Badge>
    ) : undefined;
  const hrefOf = (item) => (item.kind === "series" ? paths.series(item.slug) : paths.permalink(item.slug));
  const metaOf = (item) =>
    item.kind === "series"
      ? `${item.class_count || 0} classes`
      : [item.media === "both" ? "Audio + Video" : item.media === "video" ? "Video" : item.media === "audio" ? "Audio" : null,
         item.duration_seconds ? fmt(item.duration_seconds) : null]
          .filter(Boolean)
          .join(" · ") || null;

  const mixedRow = (item, i, arr) => (
    <ListRow
      key={`${item.kind}-${item.id}`}
      mediaType={item.media === "video" ? "video" : "audio"}
      chip={pillNode(item)}
      title={
        <Link to={hrefOf(item)} className="text-inherit no-underline hover:text-ic-blue hover:no-underline">
          {item.title}
        </Link>
      }
      meta={metaOf(item)}
      actionLabel={item.kind === "series" ? "Open series" : `Play ${item.title}`}
      onAction={() => navigate(hrefOf(item))}
      actions={
        <span className="inline-flex items-center gap-1">
          <LikeButton target={item.kind === "series" ? "series" : "class"} id={item.id} surface="bare" />
          <BookmarkButton target={item.kind === "series" ? "series" : "class"} id={item.id} surface="bare" />
        </span>
      }
      last={i === arr.length - 1}
      className="!py-3 !pl-4 !pr-3 !gap-2"
    />
  );

  if (data === null) {
    return (
      <PublicLayout>
        <div className="max-w-[960px] w-full mx-auto px-5 pt-8 flex flex-col gap-4" aria-busy="true" aria-label="Loading your dashboard">
          <div className="h-14 w-64 rounded-xl bg-ic-lightGray animate-pulse" />
          <div className="h-40 rounded-xl bg-ic-lightGray animate-pulse" />
          <div className="h-40 rounded-xl bg-ic-lightGray animate-pulse" />
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <main className="max-w-[960px] w-full mx-auto px-5 pt-5 pb-10 flex flex-col gap-7">
        {/* Identity */}
        <section className="flex items-center gap-4 py-2">
          <span aria-hidden="true" className="flex-shrink-0 w-14 h-14 rounded-full bg-ic-blue inline-flex items-center justify-center text-white text-[20px] font-extrabold">
            {initialsOf(profile.name, profile.email)}
          </span>
          <div className="flex flex-col gap-0.5 min-w-0">
            <h1 className="m-0 font-bold text-ic-navy tracking-[-0.3px] ic-wrap-anywhere" style={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.3 }}>
              {profile.name || "Your account"}
            </h1>
            {profile.member_since && <span className="text-[14px] leading-5 text-ic-slate">Member since {profile.member_since}</span>}
          </div>
        </section>

        {/* Continue where you left off */}
        <section className="flex flex-col gap-3">
          <SectionHead title="Continue where you left off" />
          {continueRows.length > 0 ? (
            <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden">
              {continueRows.map((row, i) => {
                const pct = row.duration_seconds > 0 ? Math.min(100, Math.round((row.position_seconds / row.duration_seconds) * 100)) : null;
                const minsLeft = row.duration_seconds > 0 ? Math.max(1, Math.round((row.duration_seconds - row.position_seconds) / 60)) : null;
                return (
                  <div key={row.id} className={`flex items-center gap-3 px-5 py-4 ${i === continueRows.length - 1 ? "" : "border-b border-ic-lightGray"} flex-wrap transition-colors duration-150 ease-out hover:bg-ic-offwhite`}>
                    <div className="flex-1 min-w-[220px] flex flex-col gap-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        {row.pill && <Badge>{row.pill.title}</Badge>}
                        <Link to={paths.permalink(row.slug)} className="text-[18px] font-semibold leading-7 text-black no-underline hover:text-ic-blue hover:no-underline ic-wrap-anywhere">
                          {row.title}
                        </Link>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 max-w-[320px]">
                          <ProgressBar value={pct ?? 0} height={4} />
                        </div>
                        <span className="text-[14px] leading-5 text-ic-slate flex-shrink-0">
                          {pct != null ? `▶ ${pct}%` : `▶ ${fmt(row.position_seconds)} in`}
                          {minsLeft != null && (
                            <>
                              {" · "}
                              <strong className="text-ic-body">{minsLeft} min left</strong>
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      aria-label={`Resume ${row.title}`}
                      onClick={() => navigate(paths.permalink(row.slug))}
                      className="flex-shrink-0 h-12 px-6 rounded-3xl bg-ic-blue text-ic-offwhite border-0 font-sans text-[16px] font-bold inline-flex items-center gap-2 cursor-pointer transition-all duration-150 ease-out hover:bg-ic-blueHover hover:shadow-ic-cta-hover active:bg-ic-blueActive focus-visible:outline-none focus-visible:shadow-ic-focus"
                    >
                      <PlayIcon size={16} /> Resume
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptySection
              action={
                <Link to={paths.permalink("what-is-chassidus")} className="h-12 px-6 rounded-3xl bg-ic-blue text-ic-offwhite text-[16px] font-bold inline-flex items-center gap-2 no-underline transition-all duration-150 ease-out hover:bg-ic-blueHover hover:no-underline">
                  Start here — What is Chassidus?
                </Link>
              }
            >
              Press play on any class and it will wait for you right here — exactly where you left it.
            </EmptySection>
          )}
        </section>

        {/* Bookmarks */}
        <section className="flex flex-col gap-3">
          <SectionHead title="Bookmarks" />
          {bookmarks.length > 0 ? (
            <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden">{bookmarks.map(mixedRow)}</div>
          ) : (
            <EmptySection
              icon={bookmarkIcon}
              action={
                <Link to={paths.latestClasses()} className="h-12 px-6 rounded-3xl border border-ic-blue text-ic-blue text-[16px] font-bold inline-flex items-center no-underline transition-all duration-150 ease-out hover:bg-[rgba(24,108,206,0.08)] hover:no-underline">
                  Find your next class
                </Link>
              }
            >
              Classes you bookmark will wait for you here — tap the bookmark on any class or series to save it.
            </EmptySection>
          )}
        </section>

        {/* Liked */}
        <section className="flex flex-col gap-3">
          <SectionHead title="Liked" />
          {liked.length > 0 ? (
            <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden">{liked.map(mixedRow)}</div>
          ) : (
            <EmptySection icon={heartIcon}>
              Like a class and it lands here — likes also help other learners find the best classes.
            </EmptySection>
          )}
        </section>

        {/* Quiet footer */}
        <footer className="flex items-center gap-6 pt-2 pb-6 border-t border-ic-lightGray mt-2 flex-wrap">
          <Link to={paths.home()} className="text-[14px] text-ic-blue font-semibold inline-flex items-center min-h-[44px] hover:underline">
            Newsletter preferences
          </Link>
          <button type="button" onClick={signOut} className="bg-transparent border-0 p-0 font-sans text-[14px] text-ic-slate font-semibold cursor-pointer inline-flex items-center min-h-[44px] hover:underline">
            Sign out
          </button>
        </footer>
      </main>
    </PublicLayout>
  );
}
